/**
 * VADSettings - Component for tuning Voice Activity Detection thresholds
 * 
 * Provides UI controls for adjusting VAD sensitivity and timing parameters
 */

import React, { useState, useEffect } from 'react';
import { Modal, Heading, Text } from './ui';

const VADSettings = ({ 
    isOpen, 
    onClose, 
    onThresholdsChange,
    initialThresholds = {} 
}) => {
    const [thresholds, setThresholds] = useState({
        energyThreshold: initialThresholds.energyThreshold || 0.01,
        silenceThreshold: initialThresholds.silenceThreshold || 0.005,
        silenceDuration: initialThresholds.silenceDuration || 1000,
        minSpeechDuration: initialThresholds.minSpeechDuration || 200,
        ...initialThresholds
    });

    const [isDirty, setIsDirty] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setThresholds({
                energyThreshold: initialThresholds.energyThreshold || 0.01,
                silenceThreshold: initialThresholds.silenceThreshold || 0.005,
                silenceDuration: initialThresholds.silenceDuration || 1000,
                minSpeechDuration: initialThresholds.minSpeechDuration || 200,
                ...initialThresholds
            });
            setIsDirty(false);
        }
    }, [isOpen, initialThresholds]);

    const handleThresholdChange = (key, value) => {
        const newThresholds = { ...thresholds, [key]: value };
        setThresholds(newThresholds);
        setIsDirty(true);
    };

    const handleSave = () => {
        if (onThresholdsChange) {
            onThresholdsChange(thresholds);
        }
        setIsDirty(false);
        onClose();
    };

    const handleReset = () => {
        const defaultThresholds = {
            energyThreshold: 0.01,
            silenceThreshold: 0.005,
            silenceDuration: 1000,
            minSpeechDuration: 200
        };
        setThresholds(defaultThresholds);
        setIsDirty(true);
    };

    const formatValue = (value, type) => {
        if (type === 'duration') {
            return `${value}ms`;
        }
        return value.toFixed(3);
    };

    if (!isOpen) return null;

    return (
        <Modal isOpen={isOpen} onClose={onClose}>
            <div className="max-w-2xl mx-auto">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <Heading level={2} style={{ color: '#1b263b' }}>
                            Voice Activity Detection Settings
                        </Heading>
                        <Text className="text-neutral-900 opacity-60 mt-1">
                            Adjust sensitivity and timing for speech detection
                        </Text>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-neutral-900 hover:opacity-60 transition-opacity duration-120 font-mono text-lg border-none bg-transparent p-0 m-0"
                    >
                        ✕
                    </button>
                </div>

                {/* Settings */}
                <div className="space-y-6">
                    {/* Energy Threshold */}
                    <div className="bg-neutral-50 hairline p-4">
                        <div className="flex items-center justify-between mb-3">
                            <div>
                                <Text className="font-mono text-sm uppercase tracking-wider text-neutral-900">
                                    Energy Threshold
                                </Text>
                                <Text className="text-sm text-neutral-900 opacity-60 mt-1">
                                    Minimum audio energy to detect speech start
                                </Text>
                            </div>
                            <Text className="font-mono text-sm text-neutral-900">
                                {formatValue(thresholds.energyThreshold)}
                            </Text>
                        </div>
                        <input
                            type="range"
                            min="0.001"
                            max="0.1"
                            step="0.001"
                            value={thresholds.energyThreshold}
                            onChange={(e) => handleThresholdChange('energyThreshold', parseFloat(e.target.value))}
                            className="w-full h-2 bg-neutral-200 rounded-lg appearance-none cursor-pointer"
                        />
                        <div className="flex justify-between text-xs text-neutral-900 opacity-60 mt-1">
                            <span>0.001 (Very Sensitive)</span>
                            <span>0.1 (Less Sensitive)</span>
                        </div>
                    </div>

                    {/* Silence Threshold */}
                    <div className="bg-neutral-50 hairline p-4">
                        <div className="flex items-center justify-between mb-3">
                            <div>
                                <Text className="font-mono text-sm uppercase tracking-wider text-neutral-900">
                                    Silence Threshold
                                </Text>
                                <Text className="text-sm text-neutral-900 opacity-60 mt-1">
                                    Maximum audio energy to detect speech end
                                </Text>
                            </div>
                            <Text className="font-mono text-sm text-neutral-900">
                                {formatValue(thresholds.silenceThreshold)}
                            </Text>
                        </div>
                        <input
                            type="range"
                            min="0.001"
                            max="0.05"
                            step="0.001"
                            value={thresholds.silenceThreshold}
                            onChange={(e) => handleThresholdChange('silenceThreshold', parseFloat(e.target.value))}
                            className="w-full h-2 bg-neutral-200 rounded-lg appearance-none cursor-pointer"
                        />
                        <div className="flex justify-between text-xs text-neutral-900 opacity-60 mt-1">
                            <span>0.001 (Very Sensitive)</span>
                            <span>0.05 (Less Sensitive)</span>
                        </div>
                    </div>

                    {/* Silence Duration */}
                    <div className="bg-neutral-50 hairline p-4">
                        <div className="flex items-center justify-between mb-3">
                            <div>
                                <Text className="font-mono text-sm uppercase tracking-wider text-neutral-900">
                                    Silence Duration
                                </Text>
                                <Text className="text-sm text-neutral-900 opacity-60 mt-1">
                                    How long to wait before ending speech detection
                                </Text>
                            </div>
                            <Text className="font-mono text-sm text-neutral-900">
                                {formatValue(thresholds.silenceDuration, 'duration')}
                            </Text>
                        </div>
                        <input
                            type="range"
                            min="200"
                            max="3000"
                            step="100"
                            value={thresholds.silenceDuration}
                            onChange={(e) => handleThresholdChange('silenceDuration', parseInt(e.target.value))}
                            className="w-full h-2 bg-neutral-200 rounded-lg appearance-none cursor-pointer"
                        />
                        <div className="flex justify-between text-xs text-neutral-900 opacity-60 mt-1">
                            <span>200ms (Quick End)</span>
                            <span>3000ms (Slow End)</span>
                        </div>
                    </div>

                    {/* Minimum Speech Duration */}
                    <div className="bg-neutral-50 hairline p-4">
                        <div className="flex items-center justify-between mb-3">
                            <div>
                                <Text className="font-mono text-sm uppercase tracking-wider text-neutral-900">
                                    Minimum Speech Duration
                                </Text>
                                <Text className="text-sm text-neutral-900 opacity-60 mt-1">
                                    Minimum duration to consider as valid speech
                                </Text>
                            </div>
                            <Text className="font-mono text-sm text-neutral-900">
                                {formatValue(thresholds.minSpeechDuration, 'duration')}
                            </Text>
                        </div>
                        <input
                            type="range"
                            min="50"
                            max="1000"
                            step="50"
                            value={thresholds.minSpeechDuration}
                            onChange={(e) => handleThresholdChange('minSpeechDuration', parseInt(e.target.value))}
                            className="w-full h-2 bg-neutral-200 rounded-lg appearance-none cursor-pointer"
                        />
                        <div className="flex justify-between text-xs text-neutral-900 opacity-60 mt-1">
                            <span>50ms (Very Short)</span>
                            <span>1000ms (Long)</span>
                        </div>
                    </div>
                </div>

                {/* Presets */}
                <div className="mt-6">
                    <Text className="font-mono text-sm uppercase tracking-wider text-neutral-900 mb-3">
                        Presets
                    </Text>
                    <div className="flex gap-3">
                        <button
                            onClick={() => {
                                setThresholds({
                                    energyThreshold: 0.005,
                                    silenceThreshold: 0.002,
                                    silenceDuration: 800,
                                    minSpeechDuration: 150
                                });
                                setIsDirty(true);
                            }}
                            className="px-3 py-2 bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0 transition-colors font-mono text-sm uppercase tracking-wider"
                        >
                            Sensitive
                        </button>
                        <button
                            onClick={() => {
                                setThresholds({
                                    energyThreshold: 0.01,
                                    silenceThreshold: 0.005,
                                    silenceDuration: 1000,
                                    minSpeechDuration: 200
                                });
                                setIsDirty(true);
                            }}
                            className="px-3 py-2 bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0 transition-colors font-mono text-sm uppercase tracking-wider"
                        >
                            Balanced
                        </button>
                        <button
                            onClick={() => {
                                setThresholds({
                                    energyThreshold: 0.02,
                                    silenceThreshold: 0.01,
                                    silenceDuration: 1500,
                                    minSpeechDuration: 300
                                });
                                setIsDirty(true);
                            }}
                            className="px-3 py-2 bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0 transition-colors font-mono text-sm uppercase tracking-wider"
                        >
                            Conservative
                        </button>
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-between items-center mt-8">
                    <button
                        onClick={handleReset}
                        className="px-4 py-2 bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0 transition-colors font-mono text-sm uppercase tracking-wider"
                    >
                        Reset to Defaults
                    </button>
                    
                    <div className="flex gap-3">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0 transition-colors font-mono text-sm uppercase tracking-wider"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSave}
                            disabled={!isDirty}
                            className={`px-4 py-2 font-mono text-sm uppercase tracking-wider transition-colors ${
                                isDirty
                                    ? 'bg-neutral-900 text-neutral-0 hover:opacity-60'
                                    : 'bg-neutral-200 text-neutral-500 cursor-not-allowed'
                            }`}
                        >
                            Save Settings
                        </button>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default VADSettings;

